import { endpoints } from "@/configs/Settings";
import { httpGet } from "src-next/shared/api/httpClient";

const REQUEST_TIMEOUT_MS = 30_000;

export interface WhatsAppBusinessPhoneNumberNode {
  id: string;
  display_phone_number?: string;
  verified_name?: string;
  code_verification_status?: string;
  quality_rating?: string;
  platform_type?: string;
  throughput?: {
    level?: string;
  };
  last_onboarded_time?: string;
  webhook_configuration?: {
    application?: string;
  };
}

export interface WhatsAppBusinessPhoneNumbersResponse {
  data?: WhatsAppBusinessPhoneNumberNode[];
}

export interface WhatsAppBusinessPhoneContact {
  id: string;
  displayPhoneNumber: string;
}

function normalizePhoneNumbersResponse(
  rawResponse: unknown,
): WhatsAppBusinessPhoneNumbersResponse {
  if (Array.isArray(rawResponse)) {
    return { data: rawResponse as WhatsAppBusinessPhoneNumberNode[] };
  }

  const maybeObject = rawResponse as WhatsAppBusinessPhoneNumbersResponse | null;
  if (Array.isArray(maybeObject?.data)) {
    return { data: maybeObject.data };
  }

  return { data: [] };
}

/**
 * Lista números do WABA via API Fluxmed (`GET .../whatsappbusiness/{wabaId}/phone_numbers`).
 * Autenticação: token do usuário (interceptor do `httpClient`).
 */
export async function fetchWabaPhoneNumbers(
  wabaId: string,
): Promise<WhatsAppBusinessPhoneNumbersResponse> {
  const url = `${endpoints.XDSB_domain}/whatsappbusiness/${encodeURIComponent(wabaId)}/phone_numbers`;
  const response = await httpGet<unknown>(url, {
    timeout: REQUEST_TIMEOUT_MS,
  });
  return normalizePhoneNumbersResponse(response);
}

export function pickDisplayPhoneNumbers(
  response: WhatsAppBusinessPhoneNumbersResponse,
): string[] {
  return pickPhoneContacts(response).map((phoneContact) => phoneContact.displayPhoneNumber);
}

export function pickPhoneContacts(
  response: WhatsAppBusinessPhoneNumbersResponse,
): WhatsAppBusinessPhoneContact[] {
  const phoneNumbers = response.data ?? [];
  return phoneNumbers
    .map((phoneNumber) => ({
      id: phoneNumber.id?.trim() ?? "",
      displayPhoneNumber: phoneNumber.display_phone_number?.trim() ?? "",
    }))
    .filter(
      (phoneContact) =>
        phoneContact.id.length > 0 && phoneContact.displayPhoneNumber.length > 0,
    );
}

export function pickPrimaryDisplayPhoneNumber(
  response: WhatsAppBusinessPhoneNumbersResponse,
): string | null {
  return pickDisplayPhoneNumbers(response)[0] ?? null;
}
